import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { colors, spacer, shadow, fontWeight, media, rgba } from 'settings/style';

export const OuterCard = styled.div`
  max-width: 660px;
  margin: 0 auto 68px;
  border: 1px solid ${colors.cardBorder};
  overflow: visible;
  background: ${colors.white};
  box-shadow: ${shadow.sm};
  position: relative;

  ${media.md`
    margin-bottom: 48px;
  `}
`;

export const RootSection = styled.div<{ $isOpen: boolean }>`
  position: relative;
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    background 0.22s ease;

  & + & {
    border-top: 1px solid #e7e1d8;
  }
`;

export const RootHeader = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px ${spacer.lg};
  margin: 0;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  background: ${({ $open }) => ($open ? '#faf6ee' : '#f9fafb')};
  transition: background 0.15s ease;

  &:hover {
    background: #faf6ee;
  }

  .text {
    flex: 1;
    min-width: 0;
  }

  .title-line {
    line-height: 1.3;
  }

  .title {
    font-size: 15px;
    font-weight: ${fontWeight.bold};
    color: #2a2620;
  }

  .gloss {
    font-size: 13px;
    font-weight: ${fontWeight.normal};
    color: #6d665d;
  }

  .meta {
    margin-top: 2px;
    font-size: 12px;
    color: #6d665d;
  }

  .root-chevron {
    flex-shrink: 0;
    font-size: 12px;
    color: #2a2620;
  }
`;

export const RootAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid #e7e1d8;
`;

export const SubAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid #e7e1d8;
`;

export const SubAvatarPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid #e7e1d8;
  background: #ece8e4;
`;

export const RootPanel = styled.div`
  background: ${colors.white};
  border-top: 1px solid #e7e1d8;
`;

export const NestedList = styled.div<{ $depth: number }>`
  ${({ $depth }) =>
    $depth > 0 &&
    css`
      border-top: 1px solid #f0ebe3;
    `}
`;

const subcategoryTreeInset = spacer.md;

export const NestedRowOuter = styled.div<{ $depth: number; $active: boolean; $isLast: boolean }>`
  display: flex;
  align-items: stretch;
  width: 100%;
  padding-left: calc(${spacer.lg} + ${subcategoryTreeInset} + ${({ $depth }) => $depth * 18}px);
  border: none;
  border-bottom: ${({ $isLast }) => ($isLast ? 'none' : '1px solid #e7e1d8')};
  background: ${({ $active }) => ($active ? rgba(colors.accent, 0.08) : colors.white)};

  &:hover {
    background: ${({ $active }) => ($active ? rgba(colors.accent, 0.12) : '#faf9f7')};
  }
`;

export const NestedRowMain = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px 12px 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: #2a2620;

  .label {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: ${fontWeight.bold};
  }

  .gloss {
    font-weight: ${fontWeight.normal};
    font-size: 12px;
    color: #6d665d;
  }
`;

export const NestedChevron = styled.button`
  flex-shrink: 0;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  border-left: 1px solid #f0ebe3;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  color: #6d665d;

  &:hover {
    background: ${rgba(colors.black, 0.04)};
    color: #2a2620;
  }
`;

export const HoverPreviewCard = styled.div`
  display: flex;
  max-width: 300px;
  min-height: 88px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e7e1d8;
  box-shadow: ${shadow.sm};
`;

export const HoverPreviewImage = styled.div<{ $url: string }>`
  width: 52px;
  flex-shrink: 0;
  background-image: url(${({ $url }) => $url});
  background-size: cover;
  background-position: center;
  filter: grayscale(35%) brightness(0.72);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, ${rgba(colors.black, 0.5)}, ${rgba(colors.black, 0.15)});
  }
`;

export const HoverPreviewImagePlaceholder = styled.div`
  width: 52px;
  flex-shrink: 0;
  background: #d8d3cd;
`;

export const HoverPreviewText = styled.div`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.45;
  color: #2a2620;
  background: #f4f1ec;
`;

export const HoverPreviewTitle = styled.div`
  font-weight: ${fontWeight.bold};
  font-size: 13px;
  margin-bottom: 6px;
  color: #2a2620;
`;

export const InlineGroups = styled.div<{ $depth: number; $flushLeft: boolean }>`
  padding: ${spacer.md} ${spacer.lg} ${spacer.lg};
  padding-left: ${({ $depth, $flushLeft }) =>
    $flushLeft
      ? spacer.lg
      : `calc(${spacer.lg} + ${subcategoryTreeInset} + 52px + ${$depth * 18}px)`};
  background: #fafbf9;
  border-top: 1px solid #f0ebe3;
  border-bottom: 1px solid #e7e1d8;
`;

export const PanelHint = styled.p`
  margin: 0;
  padding: ${spacer.md} ${spacer.lg};
  font-size: 12px;
  color: #8a8278;

  a {
    color: ${colors.primary300};
  }
`;

export const GroupsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacer.md};
`;

export const BrowseAll = styled(Link)`
  display: inline-block;
  margin-top: ${spacer.md};
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
`;

export const DetailBody = styled.div`
  padding: ${spacer.sm} 0 0;
`;

export const DetailCover = styled.img`
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: ${spacer.md};
  border: 1px solid #e7e1d8;
`;

export const DetailCoverPlaceholder = styled.div`
  width: 100%;
  height: 160px;
  border-radius: 12px;
  margin-bottom: ${spacer.md};
  background: #ece8e4;
  border: 1px solid #e7e1d8;
`;

export const DetailTitleBlock = styled.div`
  margin-bottom: ${spacer.sm};
`;

export const DetailTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: ${fontWeight.bold};
  color: #2a2620;
  font-family: inherit;
`;

export const DetailGloss = styled.div`
  margin-top: 4px;
  font-size: 14px;
  color: #6d665d;
`;

export const DetailDescription = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: #3d3834;
  white-space: pre-wrap;
  margin-bottom: ${spacer.lg};
`;

export const DetailActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacer.md};

  a {
    font-size: 14px;
    color: ${colors.accent};
  }
`;
